import { useMemo, useState } from "react";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  Filter,
  Plus,
  RefreshCw,
  Settings2,
  SlidersHorizontal,
  X,
} from "lucide-react";

import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type {
  TransactionLimitType,
  TransactionTierSetting,
} from "@/interfaces/transaction-tier-settings";
import { AdvertiserTier } from "@/interfaces/advertisement";
import { useToggleTransactionSetting, useTransactionSettings } from "@/hooks";
import { TransactionTierSettingDialog } from "@/components/admin-components/transaction-tier-settings/TransactionTierSettingDialog";
import { TransactionSettingActions } from "@/components/admin-components/transaction-tier-settings/TransactionSettingActions";
import { DeleteTransactionSettingDialog } from "@/components/admin-components/transaction-tier-settings/DeleteTransactionSettingDialog";
import { TransactionSettingMobileCard } from "@/components/admin-components/transaction-tier-settings/TransactionSettingMobileCard";
import Loader from "@/components/loader/Loader";
import { MobileLoadingCards } from "@/components/admin-components/transaction-tier-settings/MobileLoadingCards";
import { MobileEmptyState } from "@/components/admin-components/transaction-tier-settings/MobileEmptyState";
import { ErrorState } from "@/components/ErrorState";

const formatMoney = (value: string | number) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "$0.00";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatPercent = (value: string | number) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "0%";
  }

  return `${amount}%`;
};

const tierLabel = (tier: string) => {
  return tier
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const typeLabel = (type: TransactionLimitType) => {
  return type === "TRANSFER" ? "Transfer" : "Withdrawal";
};

export default function AdminTransactionTierSettings() {
  const [type, setType] = useState<TransactionLimitType | "ALL">("ALL");
  const [tier, setTier] = useState<AdvertiserTier | "ALL">("ALL");
  const [status, setStatus] = useState<"ALL" | "ACTIVE" | "DISABLED">("ALL");
  const [showFilters, setShowFilters] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] =
    useState<TransactionTierSetting | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [settingToDelete, setSettingToDelete] =
    useState<TransactionTierSetting | null>(null);

  const toggleMutation = useToggleTransactionSetting();

  const filters = useMemo(() => {
    return {
      ...(type !== "ALL" ? { type } : {}),
      ...(tier !== "ALL" ? { tier } : {}),
      ...(status !== "ALL" ? { enabled: status === "ACTIVE" } : {}),
    };
  }, [type, tier, status]);

  const {
    data: settings = [],
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useTransactionSettings(filters);

  const activeCount = settings.filter((setting) => setting.enabled).length;

  const transferCount = settings.filter(
    (setting) => setting.type === "TRANSFER",
  ).length;

  const withdrawalCount = settings.filter(
    (setting) => setting.type === "WITHDRAWAL",
  ).length;

  const hasFilters = type !== "ALL" || tier !== "ALL" || status !== "ALL";

  const clearFilters = () => {
    setType("ALL");
    setTier("ALL");
    setStatus("ALL");
  };

  //   if (isLoading) {
  //     return <Loader type="bounce" />;
  //   }

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className="w-full space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-teal-500/20 bg-teal-500/10">
            <Settings2 className="h-5 w-5 text-teal-400" />
          </div>

          <div>
            <h1 className="text-xl font-semibold tracking-tight text-white sm:text-2xl">
              Transaction Settings
            </h1>

            <p className="mt-1 max-w-2xl text-sm text-slate-400">
              Configure transaction limits, fees and policies across advertiser
              tiers.
            </p>
          </div>
        </div>

        <Button
          type="button"
          onClick={() => {
            setSelectedSetting(null);
            setDialogOpen(true);
          }}
          className="h-10 w-full bg-teal-500 font-medium text-slate-950 hover:bg-teal-400 sm:w-auto"
        >
          <Plus className="mr-2 h-4 w-4" />
          Create Policy
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          title="Total Policies"
          value={settings.length}
          description="Configured transaction policies"
          icon={<SlidersHorizontal className="h-5 w-5" />}
        />

        <SummaryCard
          title="Active Policies"
          value={activeCount}
          description="Currently available policies"
          icon={<CheckCircle2 className="h-5 w-5" />}
        />

        <SummaryCard
          title="Transfers"
          value={transferCount}
          description="Transfer transaction policies"
          icon={<ArrowUpFromLine className="h-5 w-5" />}
        />

        <SummaryCard
          title="Withdrawals"
          value={withdrawalCount}
          description="Withdrawal transaction policies"
          icon={<ArrowDownToLine className="h-5 w-5" />}
        />
      </div>

      <Card className="border-slate-800 bg-slate-950/70">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900">
                <Filter className="h-4 w-4 text-slate-400" />
              </div>

              <div>
                <p className="text-sm font-medium text-slate-200">
                  Policy Filters
                </p>

                <p className="text-xs text-slate-500">
                  Narrow down transaction policies
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFilters((previous) => !previous)}
                className="border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white lg:hidden"
              >
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                Filters
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => refetch()}
                disabled={isFetching}
                className="border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
                />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>

          <div className={`mt-4 ${showFilters ? "block" : "hidden lg:block"}`}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <FilterSelect
                label="Transaction Type"
                value={type}
                onChange={(value) =>
                  setType(value as TransactionLimitType | "ALL")
                }
                options={[
                  { value: "ALL", label: "All transaction types" },
                  { value: "TRANSFER", label: "Transfer" },
                  { value: "WITHDRAWAL", label: "Withdrawal" },
                ]}
              />

              <FilterSelect
                label="Advertiser Tier"
                value={tier}
                onChange={(value) => setTier(value as AdvertiserTier | "ALL")}
                options={[
                  { value: "ALL", label: "All tiers" },
                  { value: AdvertiserTier.BASIC, label: "Basic" },
                  { value: AdvertiserTier.VERIFIED, label: "Verified" },
                  { value: AdvertiserTier.TRUSTED, label: "Trusted" },
                  {
                    value: AdvertiserTier.BUSINESS_BASIC,
                    label: "Business Basic",
                  },
                  {
                    value: AdvertiserTier.BUSINESS_VERIFIED,
                    label: "Business Verified",
                  },
                  {
                    value: AdvertiserTier.BUSINESS_ENTERPRISE,
                    label: "Business Enterprise",
                  },
                ]}
              />

              <FilterSelect
                label="Status"
                value={status}
                onChange={(value) =>
                  setStatus(value as "ALL" | "ACTIVE" | "DISABLED")
                }
                options={[
                  { value: "ALL", label: "All statuses" },
                  { value: "ACTIVE", label: "Active" },
                  { value: "DISABLED", label: "Disabled" },
                ]}
              />
            </div>

            {hasFilters && (
              <div className="mt-3 flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={clearFilters}
                  className="text-xs text-slate-400 hover:text-white"
                >
                  <X className="mr-1.5 h-3.5 w-3.5" />
                  Clear filters
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden border-slate-800 bg-slate-950/70">
        <div className="flex items-center justify-between border-b border-slate-800 px-4 py-4 sm:px-6">
          <div>
            <h2 className="text-sm font-semibold text-white">
              Transaction Policies
            </h2>

            <p className="mt-1 text-xs text-slate-500">
              {settings.length} {settings.length === 1 ? "policy" : "policies"}{" "}
              configured
            </p>
          </div>

          <CircleDollarSign className="h-5 w-5 text-teal-400" />
        </div>

        <div className="overflow-x-auto hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="whitespace-nowrap text-xs uppercase tracking-wider text-slate-500">
                  Tier
                </TableHead>

                <TableHead className="whitespace-nowrap text-xs uppercase tracking-wider text-slate-500">
                  Type
                </TableHead>

                <TableHead className="whitespace-nowrap text-xs uppercase tracking-wider text-slate-500">
                  Transaction Range
                </TableHead>

                <TableHead className="whitespace-nowrap text-xs uppercase tracking-wider text-slate-500">
                  Fee
                </TableHead>

                <TableHead className="whitespace-nowrap text-xs uppercase tracking-wider text-slate-500">
                  Daily
                </TableHead>

                <TableHead className="whitespace-nowrap text-xs uppercase tracking-wider text-slate-500">
                  Monthly
                </TableHead>

                <TableHead className="whitespace-nowrap text-xs uppercase tracking-wider text-slate-500">
                  Status
                </TableHead>

                <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading ? (
                <LoadingRows />
              ) : settings.length === 0 ? (
                <EmptyState />
              ) : (
                settings.map((setting) => (
                  <TransactionSettingRow
                    key={setting.id}
                    setting={setting}
                    onEdit={(selected) => {
                      setSelectedSetting(selected);
                      setDialogOpen(true);
                    }}
                    onDelete={(selected) => {
                      setSettingToDelete(selected);
                      setDeleteDialogOpen(true);
                    }}
                    onToggle={(selected) => {
                      toggleMutation.mutate({
                        id: selected.id,
                        enabled: !selected.enabled,
                      });
                    }}
                    isToggling={
                      toggleMutation.isPending &&
                      toggleMutation.variables?.id === setting.id
                    }
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <div className="space-y-3 lg:hidden">
          {isLoading ? (
            <MobileLoadingCards />
          ) : settings.length === 0 ? (
            <MobileEmptyState />
          ) : (
            settings.map((setting) => (
              <TransactionSettingMobileCard
                key={setting.id}
                setting={setting}
                onEdit={() => {
                  setSelectedSetting(setting);
                  setDialogOpen(true);
                }}
                onToggle={() => {
                  toggleMutation.mutate({
                    id: setting.id,
                    enabled: !setting.enabled,
                  });
                }}
                onDelete={() => {
                  setSettingToDelete(setting);
                  setDeleteDialogOpen(true);
                }}
              />
            ))
          )}
        </div>
      </Card>

      <TransactionTierSettingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        setting={selectedSetting}
      />

      <DeleteTransactionSettingDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        setting={settingToDelete}
      />
    </div>
  );
}

function SummaryCard({
  title,
  value,
  description,
  icon,
}: {
  title: string;
  value: number;
  description: string;
  icon: React.ReactNode;
}) {
  return (
    <Card className="border-slate-800 bg-slate-950/70">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
              {title}
            </p>

            <p className="mt-2 text-2xl font-semibold tracking-tight text-white">
              {value}
            </p>
          </div>

          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-teal-500/20 bg-teal-500/10 text-teal-400">
            {icon}
          </div>
        </div>

        <p className="mt-3 text-xs text-slate-500">{description}</p>
      </CardContent>
    </Card>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: {
    value: string;
    label: string;
  }[];
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-slate-400">{label}</label>

      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-10 border-slate-700 bg-slate-900 text-slate-200">
          <SelectValue />
        </SelectTrigger>

        <SelectContent className="border-slate-700 bg-slate-900 text-slate-200">
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function TransactionSettingRow({
  setting,
  onEdit,
  onDelete,
  onToggle,
  isToggling,
}: {
  setting: TransactionTierSetting;
  onEdit: (setting: TransactionTierSetting) => void;
  onDelete: (setting: TransactionTierSetting) => void;
  onToggle: (setting: TransactionTierSetting) => void;
  isToggling: boolean;
}) {
  return (
    <TableRow className="border-slate-800 transition-colors hover:bg-slate-900/60">
      <TableCell>
        <div>
          <p className="font-medium text-white">{tierLabel(setting.tier)}</p>

          <p className="mt-0.5 text-[11px] text-slate-500">Tier policy</p>
        </div>
      </TableCell>

      <TableCell>
        <Badge
          variant="outline"
          className={
            setting.type === "TRANSFER"
              ? "border-blue-500/20 bg-blue-500/10 text-blue-400"
              : "border-purple-500/20 bg-purple-500/10 text-purple-400"
          }
        >
          {typeLabel(setting.type)}
        </Badge>
      </TableCell>

      <TableCell>
        <div className="whitespace-nowrap">
          <p className="text-sm font-medium text-slate-200">
            {formatMoney(setting.minAmount)}
          </p>

          <p className="text-xs text-slate-500">
            to {formatMoney(setting.maxAmount)}
          </p>
        </div>
      </TableCell>

      <TableCell>
        <div className="whitespace-nowrap">
          <p className="text-sm font-medium text-slate-200">
            {formatPercent(setting.feePercent)}
          </p>

          <p className="text-xs text-slate-500">
            Cap {formatMoney(setting.feeCap)}
          </p>
        </div>
      </TableCell>

      <TableCell className="whitespace-nowrap text-sm text-slate-300">
        {formatMoney(setting.dailyLimit)}
      </TableCell>

      <TableCell className="whitespace-nowrap text-sm text-slate-300">
        {formatMoney(setting.monthlyLimit)}
      </TableCell>

      <TableCell>
        {setting.enabled ? (
          <Badge className="border border-emerald-500/20 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/10">
            Active
          </Badge>
        ) : (
          <Badge className="border border-slate-700 bg-slate-800 text-slate-400 hover:bg-slate-800">
            Disabled
          </Badge>
        )}
      </TableCell>

      <TableCell className="text-right">
        <TransactionSettingActions
          setting={setting}
          onEdit={() => onEdit(setting)}
          onToggle={() => onToggle(setting)}
          onDelete={() => onDelete(setting)}
          isToggling={isToggling}
        />
      </TableCell>
    </TableRow>
  );
}

function LoadingRows() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, index) => (
        <TableRow key={index} className="border-slate-800">
          {Array.from({ length: 8 }).map((__, cell) => (
            <TableCell key={cell}>
              <div className="h-4 w-20 animate-pulse rounded bg-slate-800" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  return (
    <TableRow>
      <TableCell colSpan={8} className="h-56 text-center">
        <div className="flex flex-col items-center justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900">
            <Settings2 className="h-5 w-5 text-slate-500" />
          </div>

          <p className="mt-4 text-sm font-medium text-slate-300">
            No transaction policies found
          </p>

          <p className="mt-1 max-w-sm text-xs text-slate-500">
            Create a transaction policy or adjust your filters to see available
            policies.
          </p>
        </div>
      </TableCell>
    </TableRow>
  );
}
